import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useLanguage } from 'shared/context/LanguageContext';
import { cn } from 'shared/lib/utils';

interface StepNavigationProps {
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  isSubmitting?: boolean;
  hideBack?: boolean;
  hideNext?: boolean;
  nextDisabled?: boolean;
  nextType?: 'button' | 'submit';
  /** Compact spacing when rendered inside a sticky panel footer. */
  compact?: boolean;
}

const StepNavigation: React.FC<StepNavigationProps> = ({
  onBack,
  onNext,
  nextLabel,
  isSubmitting,
  hideBack,
  hideNext,
  nextDisabled,
  nextType = 'button',
  compact = false,
}) => {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const NextIcon = dir === 'rtl' ? ArrowLeft : ArrowRight;
  const BackIcon = dir === 'rtl' ? ArrowRight : ArrowLeft;

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3',
        compact ? '' : 'mt-8 border-t border-gray-200/70 pt-6',
      )}
    >
      <button
        type="button"
        onClick={onBack}
        disabled={hideBack || isSubmitting}
        className={cn(
          'inline-flex items-center gap-2 rounded-xl border border-transparent px-4 py-2.5 text-sm font-medium',
          'text-gray-600 hover:border-[#bf0035]/10 hover:bg-[#bf0035]/[0.06] hover:text-[#bf0035]',
          'transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bf0035] focus-visible:ring-offset-2',
          hideBack && 'invisible',
        )}
      >
        <BackIcon size={16} />
        {t('common.back')}
      </button>

      {!hideNext && (
        <button
          type={nextType}
          onClick={nextType === 'button' ? onNext : undefined}
          disabled={isSubmitting || nextDisabled}
          className={cn(
            'inline-flex items-center gap-2 rounded-xl bg-[#bf0035] px-6 py-3 text-sm font-semibold text-white sm:text-base',
            'transition-colors hover:bg-[#bf0035]/90',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bf0035] focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-60',
          )}
        >
          {isSubmitting ? (
            <>
              <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
              <span>{t('common.loading')}</span>
            </>
          ) : (
            <>
              <span>{nextLabel || t('common.next')}</span>
              <NextIcon size={18} />
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default StepNavigation;
